<template>
  <div class="category-admin">
    <div class="toolbar">
      <div class="toolbar-left">
        <input
          v-model.trim="keyword"
          type="search"
          class="search-input"
          placeholder="搜索描述…"
          @keyup.enter="loadList"
        />
        <button type="button" class="btn btn-secondary" @click="loadList">搜索</button>
        <button v-if="keyword" type="button" class="btn btn-ghost" @click="resetSearch">重置</button>
      </div>
      <button type="button" class="btn btn-primary" @click="openCreate">+ 新增分类</button>
    </div>

    <p v-if="error" class="alert alert-error">{{ error }}</p>
    <p v-if="success" class="alert alert-success">{{ success }}</p>

    <div v-if="loading" class="loading">加载中…</div>

    <div v-else class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th class="col-sort">排序</th>
            <th class="col-image">图片</th>
            <th>英文名称</th>
            <th>中文名称</th>
            <th>描述</th>
            <th class="col-time">更新时间</th>
            <th class="col-actions">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!items.length">
            <td colspan="7" class="empty-cell">暂无分类，点击「新增分类」添加</td>
          </tr>
          <tr v-for="(row, index) in items" :key="row.id">
            <td class="col-sort">{{ index + 1 }}</td>
            <td class="col-image">
              <img
                v-if="categoryThumbUrl(row)"
                :src="categoryThumbUrl(row)"
                :alt="row.name_en"
                class="thumb"
                @error="onThumbError($event)"
              />
              <span v-else class="thumb-placeholder">无图</span>
            </td>
            <td>{{ row.name_en }}</td>
            <td>{{ row.name_zh || '—' }}</td>
            <td class="col-desc">
              <span class="desc-text" :title="row.description">{{ row.description || '—' }}</span>
            </td>
            <td class="col-time">{{ formatTime(row.updated_at) }}</td>
            <td class="col-actions">
              <button type="button" class="btn-link" @click="openEdit(row)">编辑</button>
              <button type="button" class="btn-link danger" @click="confirmDelete(row)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="table-footer">共 {{ items.length }} 条 · 按更新时间倒序</p>
    </div>

    <div v-if="modalVisible" class="modal-mask" @click.self="closeModal">
      <div class="modal">
        <h3 class="modal-title">{{ editingId ? '编辑分类' : '新增分类' }}</h3>
        <form class="modal-form" @submit.prevent="submitForm">
          <div class="form-row">
            <label>分类图片</label>
            <div class="image-upload">
              <div v-if="imagePreview" class="image-preview-wrap">
                <img :src="imagePreview" alt="分类预览" class="image-preview" />
                <button type="button" class="btn-link danger" @click="removeImage">移除图片</button>
              </div>
              <p v-else class="image-hint">支持 JPG、PNG、GIF、WEBP，最大 5MB</p>
              <input
                ref="imageInput"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                class="file-input"
                @change="onImageSelected"
              />
            </div>
          </div>
          <div class="form-row">
            <label>英文名称 <span class="req">*</span></label>
            <input v-model.trim="form.name_en" type="text" class="input-lg" required />
          </div>
          <div class="form-row">
            <label>中文名称</label>
            <input v-model.trim="form.name_zh" type="text" class="input-lg" />
          </div>
          <div class="form-row">
            <label>描述 <span class="req">*</span></label>
            <textarea
              v-model.trim="form.description"
              class="textarea-lg"
              rows="8"
              placeholder="分类说明，支持列表搜索"
              required
            ></textarea>
          </div>
          <p v-if="formError" class="alert alert-error">{{ formError }}</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" @click="closeModal">取消</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? '保存中…' : '保存' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {
  createCategory,
  deleteCategory,
  fetchCategories,
  resolveUploadUrl,
  updateCategory,
  uploadCategoryImage
} from '@/api/categories'

const emptyForm = () => ({
  name_en: '',
  name_zh: '',
  description: '',
  image_path: ''
})

export default {
  name: 'ProductCategories',
  data() {
    return {
      items: [],
      keyword: '',
      loading: false,
      saving: false,
      error: '',
      success: '',
      modalVisible: false,
      editingId: null,
      form: emptyForm(),
      formError: '',
      pendingFile: null,
      imagePreview: '',
      previewObjectUrl: ''
    }
  },
  beforeUnmount() {
    this.revokePreviewUrl()
  },
  created() {
    this.loadList()
  },
  methods: {
    categoryThumbUrl(row) {
      if (!row) return ''
      return resolveUploadUrl(row.image_url || row.image_path || '')
    },
    onThumbError(event) {
      event.target.style.display = 'none'
    },
    revokePreviewUrl() {
      if (this.previewObjectUrl) {
        URL.revokeObjectURL(this.previewObjectUrl)
        this.previewObjectUrl = ''
      }
    },
    resetImageState() {
      this.pendingFile = null
      this.revokePreviewUrl()
      this.imagePreview = ''
      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = ''
      }
    },
    onImageSelected(event) {
      const file = event.target.files?.[0]
      if (!file) return
      const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      if (!allowed.includes(file.type)) {
        this.formError = '仅支持 JPG、PNG、GIF、WEBP 图片'
        event.target.value = ''
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.formError = '图片不能超过 5MB'
        event.target.value = ''
        return
      }
      this.formError = ''
      this.pendingFile = file
      this.revokePreviewUrl()
      this.previewObjectUrl = URL.createObjectURL(file)
      this.imagePreview = this.previewObjectUrl
    },
    removeImage() {
      this.form.image_path = ''
      this.resetImageState()
    },
    formatTime(value) {
      if (!value) return '—'
      const d = new Date(value.replace(' ', 'T'))
      if (Number.isNaN(d.getTime())) return value
      return d.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    async loadList() {
      this.loading = true
      this.error = ''
      try {
        const res = await fetchCategories(this.keyword)
        this.items = res.data?.items || []
      } catch (e) {
        this.error = e.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    resetSearch() {
      this.keyword = ''
      this.loadList()
    },
    openCreate() {
      this.editingId = null
      this.form = emptyForm()
      this.formError = ''
      this.resetImageState()
      this.modalVisible = true
    },
    openEdit(row) {
      this.editingId = row.id
      this.form = {
        name_en: row.name_en,
        name_zh: row.name_zh || '',
        description: row.description || '',
        image_path: row.image_path || ''
      }
      this.formError = ''
      this.resetImageState()
      this.imagePreview = this.categoryThumbUrl(row)
      this.modalVisible = true
    },
    closeModal() {
      this.modalVisible = false
      this.formError = ''
      this.resetImageState()
    },
    async submitForm() {
      this.formError = ''
      if (!this.form.name_en || !this.form.description) {
        this.formError = '请填写英文名称和描述'
        return
      }
      this.saving = true
      try {
        let imagePath = this.form.image_path || ''
        if (this.pendingFile) {
          const uploadRes = await uploadCategoryImage(this.pendingFile)
          imagePath = uploadRes.data?.image_path || ''
        }

        const payload = {
          name_en: this.form.name_en,
          name_zh: this.form.name_zh,
          description: this.form.description,
          image_path: imagePath
        }
        if (this.editingId) {
          await updateCategory(this.editingId, payload)
          this.success = '分类已更新'
        } else {
          await createCategory(payload)
          this.success = '分类已创建'
        }
        this.closeModal()
        await this.loadList()
        setTimeout(() => {
          this.success = ''
        }, 2500)
      } catch (e) {
        this.formError = e.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
    confirmDelete(row) {
      const name = row.name_zh || row.name_en
      if (!window.confirm(`确定删除分类「${name}」？`)) return
      this.doDelete(row.id)
    },
    async doDelete(id) {
      this.error = ''
      try {
        await deleteCategory(id)
        this.success = '已删除'
        await this.loadList()
        setTimeout(() => {
          this.success = ''
        }, 2500)
      } catch (e) {
        this.error = e.message || '删除失败'
      }
    }
  }
}
</script>

<style scoped>
.category-admin {
  max-width: 1200px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.toolbar-left {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.search-input {
  width: 240px;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.btn {
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  border: none;
  cursor: pointer;
}

.btn-primary {
  background: #0d9488;
  color: #fff;
}

.btn-primary:hover {
  background: #0f766e;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #fff;
  color: #334155;
  border: 1px solid #e2e8f0;
}

.btn-ghost {
  background: transparent;
  color: #64748b;
}

.alert {
  margin: 0 0 12px;
  padding: 10px 14px;
  font-size: 13px;
  border-radius: 8px;
}

.alert-error {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.alert-success {
  color: #047857;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}

.loading {
  padding: 40px;
  text-align: center;
  color: #64748b;
}

.table-wrap {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th,
.data-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}

.data-table th {
  font-weight: 600;
  color: #475569;
  background: #f8fafc;
  font-size: 13px;
}

.data-table tbody tr:hover {
  background: #fafbfc;
}

.col-sort {
  width: 56px;
  text-align: center;
  color: #64748b;
  font-variant-numeric: tabular-nums;
}

.col-image {
  width: 88px;
}

.thumb {
  display: block;
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.thumb-placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  font-size: 12px;
  color: #94a3b8;
  background: #f1f5f9;
  border-radius: 8px;
}

.col-desc {
  max-width: 320px;
}

.desc-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  color: #475569;
}

.col-time {
  width: 150px;
  white-space: nowrap;
  color: #64748b;
  font-size: 13px;
}

.col-actions {
  width: 100px;
  white-space: nowrap;
}

.btn-link {
  margin-right: 10px;
  padding: 0;
  font-size: 13px;
  color: #0d9488;
  background: none;
  border: none;
  cursor: pointer;
}

.btn-link:hover {
  text-decoration: underline;
}

.btn-link.danger {
  padding: 0;
  font-size: 13px;
  color: #dc2626;
  background: none;
  border: none;
  cursor: pointer;
}

.empty-cell {
  text-align: center;
  color: #94a3b8;
  padding: 32px !important;
}

.table-footer {
  margin: 0;
  padding: 10px 14px;
  font-size: 12px;
  color: #94a3b8;
}

.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.45);
}

.modal {
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px 32px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.15);
}

.modal-title {
  margin: 0 0 20px;
  font-size: 18px;
  color: #0f172a;
}

.modal-form label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.req {
  color: #dc2626;
}

.modal-form input,
.modal-form textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  font-size: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.modal-form .input-lg {
  padding: 12px 14px;
  font-size: 15px;
}

.modal-form .textarea-lg {
  min-height: 180px;
  padding: 12px 14px;
  font-size: 15px;
  line-height: 1.6;
  resize: vertical;
}

.form-row {
  margin-bottom: 16px;
}

.image-upload {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.image-preview-wrap {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.image-preview {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.image-hint {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.file-input {
  font-size: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
